import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { getBackendUrl } from "../../config";

const LABEL_FIELD_PREFIX = "Etiquetas-";

const isLabelExtraInfo = infoOrName => {
	const name = typeof infoOrName === "string" ? infoOrName : infoOrName?.name;
	return String(name || "").startsWith(LABEL_FIELD_PREFIX);
};

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		gap: theme.spacing(1),
	},

	fileInfo: {
		flex: 1,
		minWidth: 0,
	},

	fileActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		flexWrap: "wrap",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muy corto")
		.max(50, "Muy largo")
		.required("Requerido"),
	number: Yup.string().min(8, "Muy corto").max(50, "Muy largo"),
	email: Yup.string().email("Correo inválido"),
});

const mergeExtraInfoWithCustomFields = (contactData, customFieldNames = []) => {
	const extraInfo = Array.isArray(contactData.extraInfo) ? contactData.extraInfo : [];
	const existingNames = new Set(
		extraInfo.map(info => String(info?.name || "").trim()).filter(Boolean)
	);
	const missingFields = customFieldNames
		.map(name => String(name || "").trim())
		.filter(name => name && !isLabelExtraInfo(name) && !existingNames.has(name))
		.map(name => ({ name, value: "" }));

	return {
		...contactData,
		extraInfo: [...extraInfo, ...missingFields],
	};
};

const normalizeCustomFieldName = value =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

const getPublicFileUrl = fileUrl => {
	const value = String(fileUrl || "").trim();
	if (!value) return "";
	if (/^https?:\/\//i.test(value)) return value;
	const baseUrl = String(getBackendUrl() || "").replace(/\/+$/, "");
	const path = value.replace(/^\/+/, "");
	return `${baseUrl}/${path}`;
};

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		extraInfo: [],
	};

	const [contact, setContact] = useState(initialState);
	const [deletingCustomField, setDeletingCustomField] = useState(null);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (!open) return;

			let customFieldNames = [];

			try {
				const { data } = await api.get("/contacts/custom-fields/names");
				customFieldNames = data.fields || [];
			} catch (err) {
				toastError(err);
			}

			if (initialValues) {
				setContact(prevState => {
					return mergeExtraInfoWithCustomFields(
						{ ...prevState, ...initialValues },
						customFieldNames
					);
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(mergeExtraInfoWithCustomFields(data, customFieldNames));
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setDeletingCustomField(null);
	};

	const handleSaveContact = async values => {
		try {
			const formData = new FormData();
			const normalizedExtraInfo = (values.extraInfo || [])
				.filter(info => {
					const hasName = String(info?.name || "").trim();
					const hasValue = String(info?.value || "").trim();
					return hasName || hasValue || info?.file || info?.fileUrl;
				});
			const payload = {
				...values,
				extraInfo: normalizedExtraInfo.map(info => ({
					id: info.id,
					name: info.name || "",
					value: info.fieldType === "file" ? info.fileUrl || "" : info.value || "",
					fieldType: info.fieldType === "file" ? "file" : "text",
					fileUrl: info.fileUrl || "",
					fileName: info.fileName || "",
					mimeType: info.mimeType || "",
				})),
			};

			formData.append("data", JSON.stringify(payload));
			normalizedExtraInfo.forEach((info, index) => {
				if (info?.fieldType === "file" && info.file) {
					formData.append(`contactFile_${index}`, info.file);
				}
			});

			if (contactId) {
				await api.put(`/contacts/${contactId}`, formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});
				handleClose();
			} else {
				const { data } = await api.post("/contacts", formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteCustomFieldName = async (fieldName, values, setFieldValue) => {
		const normalizedName = normalizeCustomFieldName(fieldName);

		try {
			await api.delete(`/contacts/custom-fields/${encodeURIComponent(fieldName)}`);
			setFieldValue(
				"extraInfo",
				(values.extraInfo || []).filter(
					info => normalizeCustomFieldName(info?.name) !== normalizedName
				)
			);
			toast.success("Campo personalizado eliminado.");
		} catch (err) {
			toastError(err);
		} finally {
			setDeletingCustomField(null);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<ConfirmationModal
								title="Eliminar campo personalizado"
								open={Boolean(deletingCustomField)}
								onClose={() => setDeletingCustomField(null)}
								onConfirm={() =>
									handleDeleteCustomFieldName(
										deletingCustomField?.name,
										values,
										setFieldValue
									)
								}
							>
								{`Este campo se eliminará de todos los contactos: ${
									deletingCustomField?.name || ""
								}`}
							</ConfirmationModal>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.number")}
									name="number"
									error={touched.number && Boolean(errors.number)}
									helperText={touched.number && errors.number}
									placeholder="5513912344321"
									variant="outlined"
									margin="dense"
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => isLabelExtraInfo(info) ? null : (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														{info.fieldType === "file" ? (
															<div className={classes.fileInfo}>
																<Typography variant="caption" color="textSecondary">
																	{info.file?.name || info.fileName || "Sin archivo"}
																</Typography>
																<div className={classes.fileActions}>
																	<Button
																		component="label"
																		size="small"
																		variant="outlined"
																		startIcon={<AttachFileIcon />}
																	>
																		{info.file || info.fileUrl ? "Reemplazar" : "Subir"}
																		<input
																			type="file"
																			hidden
																			onChange={event => {
																				const file = event.target.files?.[0] || null;
																				event.target.value = "";
																				setFieldValue(`extraInfo[${index}].file`, file);
																				if (file) {
																					setFieldValue(`extraInfo[${index}].fileName`, file.name);
																					setFieldValue(`extraInfo[${index}].mimeType`, file.type);
																				}
																			}}
																		/>
																	</Button>
																	{info.fileUrl && (
																		<Button
																			size="small"
																			variant="outlined"
																			href={getPublicFileUrl(info.fileUrl)}
																			target="_blank"
																			rel="noopener noreferrer"
																		>
																			Ver
																		</Button>
																	)}
																</div>
															</div>
														) : (
															<Field
																as={TextField}
																label={i18n.t("contactModal.form.extraValue")}
																name={`extraInfo[${index}].value`}
																variant="outlined"
																margin="dense"
																className={classes.textField}
															/>
														)}
														<IconButton
															size="small"
															onClick={() => {
																const fieldName = String(info?.name || "").trim();
																if (!fieldName) {
																	remove(index);
																	return;
																}
																setDeletingCustomField({ name: fieldName });
															}}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "", fieldType: "text" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													startIcon={<AttachFileIcon />}
													onClick={() =>
														push({
															name: "",
															value: "",
															fieldType: "file",
															fileUrl: "",
															fileName: "",
															mimeType: "",
															file: null,
														})
													}
												>
													Agregar archivo
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;

